import React, { useEffect, useState } from 'react'
import { ActivityIndicator, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Button, ContextualDropdown, Icon, Screen, Text } from '@components'
import { Theme } from '@theme'
import { useCreationStackIntegration, useScreen } from '@hooks'
import { useLanguage } from '@context'
import { digestDelayedUntil, getCommonStyles, showApiErrorToast, showSuccessToast } from '@utils'
import { useSafeNavigation } from '../../navigation/web/useSafeNavigation'
import {
  useNotificationSettings,
  useUpdateNotificationSettings,
  type NotificationSettings as Settings,
} from '../../hooks/api/queries/useNotificationSettings'

type Field = keyof Settings

// The choices of each setting, in the order they are offered.
const CHOICES: { [K in Field]: ReadonlyArray<Settings[K]> } = {
  keepContact: ['onTime', 'morning', 'off'],
  birthdays: ['eveAndDay', 'day', 'off'],
  events: ['15', '30', '60', '120', 'off'],
  messages: ['always', 'exceptNight', 'off'],
  requests: ['always', 'exceptNight', 'off'],
  morningDigest: ['off', '7', '8', '9'],
  quietHours: ['off', '21-7', '22-7', '22-8', '23-7', '23-8'],
}

// Where the words of each choice are (messages and requests share theirs).
const CHOICE_KEYS: Record<Field, string> = {
  keepContact: 'keepContact',
  birthdays: 'birthdays',
  events: 'events',
  messages: 'live',
  requests: 'live',
  morningDigest: 'morningDigest',
  quietHours: 'quietHours',
}

/**
 * « Réglages des notifications »: when the phone rings for follow-ups,
 * birthdays, rendez-vous, messages and requests, the morning summary and the
 * quiet night. Opened by the gear of the Notifications page, in the side
 * panel on a large screen.
 */
const NotificationSettings: React.FC = () => {
  const theme = useTheme()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const { t } = useLanguage()
  const { isMobile, isWeb } = useScreen()
  const navigation = useSafeNavigation()
  const { hideCreationStack, isVisible } = useCreationStackIntegration()

  const { data: saved } = useNotificationSettings()
  const { mutate: save, isPending } = useUpdateNotificationSettings()
  const [draft, setDraft] = useState<Settings | null>(null)

  useEffect(() => {
    if (saved && !draft) setDraft(saved)
  }, [saved, draft])

  const close = () => {
    if (isVisible) hideCreationStack()
    else navigation?.goBack()
  }

  const handleSave = () => {
    if (!draft) return
    save(draft, {
      onSuccess: () => {
        showSuccessToast(t('toast.success'), t('notifications.toasts.settingsSaved'))
        close()
      },
      onError: (error) => showApiErrorToast(t, error),
    })
  }

  const labelOf = (field: Field, value: string) =>
    t(`notificationSettings.choices.${CHOICE_KEYS[field]}.${value}`)

  // A summary chosen before the end of the night waits for it: say when it comes.
  const digestDelayed = draft ? digestDelayedUntil(draft) : null

  const row = (field: Field) => {
    if (!draft) return null
    const options = CHOICES[field].map((value) => ({ id: value, name: labelOf(field, value) }))
    return (
      <View style={styles.row} testID={`notification-settings-${field}`}>
        <Text type="bodySmall" style={styles.rowLabel}>
          {t(`notificationSettings.${field}`)}
        </Text>
        <View style={styles.dropdown}>
          <ContextualDropdown
            data={options}
            label={labelOf(field, draft[field])}
            selectedId={draft[field]}
            onSelect={(id) => setDraft({ ...draft, [field]: id } as Settings)}
          />
        </View>
      </View>
    )
  }

  return (
    <Screen>
      <View style={[styles.header, isWeb && commonStyles.paddingTop]}>
        {isMobile ? (
          <TouchableOpacity onPress={close} accessibilityRole="button">
            <Icon name="arrowBack" size={25} />
          </TouchableOpacity>
        ) : (
          <View style={styles.placeholder} />
        )}
        <Text type="titleBold">{t('notificationSettings.title')}</Text>
        <View style={styles.placeholder} />
      </View>

      {!draft ? (
        <ActivityIndicator color={theme.colors.primary} style={styles.loader} />
      ) : (
        <ScrollView
          style={styles.scrollContent}
          contentContainerStyle={commonStyles.paddingBottom}
          showsVerticalScrollIndicator={false}
        >
          <Text type="bodyBold" style={styles.sectionTitle}>
            {t('notificationSettings.sectionPhone')}
          </Text>
          {row('keepContact')}
          {row('birthdays')}
          {row('events')}
          {row('messages')}
          {row('requests')}

          <Text type="bodyBold" style={[styles.sectionTitle, styles.sectionSpacing]}>
            {t('notificationSettings.sectionQuiet')}
          </Text>
          {row('morningDigest')}
          <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.note}>
            {t('notificationSettings.digestNote')}
          </Text>
          {digestDelayed !== null ? (
            <Text
              type="bodySmall"
              color={theme.colors.greyBlue}
              style={styles.note}
              testID="notification-settings-digest-delay"
            >
              {t('notificationSettings.digestDelayed', { hour: `${digestDelayed}:00` })}
            </Text>
          ) : null}
          {row('quietHours')}
          <Text type="bodySmall" color={theme.colors.greyBlue} style={styles.note}>
            {t('notificationSettings.quietNote')}
          </Text>

          <View style={styles.footer}>
            <Button
              testID="notification-settings-save"
              title={t('common.validate')}
              disabled={isPending}
              onPress={handleSave}
            />
          </View>
        </ScrollView>
      )}
    </Screen>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 16,
      paddingBottom: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    placeholder: {
      width: 25,
    },
    loader: {
      marginTop: theme.spacing.xl,
    },
    scrollContent: {
      flex: 1,
      padding: theme.spacing.lg,
    },
    sectionTitle: {
      marginBottom: theme.spacing.xs,
    },
    sectionSpacing: {
      marginTop: theme.spacing.xl,
    },
    row: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing.md,
      marginTop: theme.spacing.md,
    },
    rowLabel: {
      flex: 1,
    },
    dropdown: {
      flexDirection: 'row',
      width: 200,
    },
    note: {
      marginTop: theme.spacing.sm,
    },
    footer: {
      marginTop: theme.spacing.xl,
    },
  })

export default NotificationSettings
